'use client';

import { useState } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';

interface PhotoEventHeroProps {
  eventName: string;
  /** A short label above the title, in sentence case: what this page is. */
  eyebrow: string;
  /** One line under the title. */
  subtitle?: string;
  /** Optional chip beside that: the price. */
  badge?: string;
  coverImageUrl?: string;
  mobileCoverImageUrl?: string;
  /**
   * Where the banner is anchored in the band at each breakpoint, as CSS
   * object-position (the organiser drags it in Photos > Sharing). Centre when
   * absent.
   */
  bannerPosition?: { desktop: string; mobile: string };
  /** Kept for callers; the v8 header no longer paints a gradient band. */
  primary?: string;
  secondary?: string;
}

/**
 * The top of every public photo page: the organiser's banner, then the page's
 * title on the page itself.
 *
 * The title used to sit on the photograph on darkened pills, because an
 * organiser's banner is chosen to look good, not to be typed on. Under the
 * image it needs no pill and reads on any banner. With no banner there is no
 * stand-in band, only the title.
 */
export function PhotoEventHero({ eventName, eyebrow, subtitle, badge, coverImageUrl, mobileCoverImageUrl, bannerPosition }: PhotoEventHeroProps) {
  const isMobile = useIsMobile();
  // A phone takes a much tighter slice of this band, so an organiser can supply
  // a crop composed for it.
  const image = (isMobile && mobileCoverImageUrl) || coverImageUrl;
  // A banner that fails to load takes its band with it, never a broken image.
  const [failed, setFailed] = useState<string | null>(null);

  return (
    <div>
      {image && failed !== image && (
        // The phone band is the 390 x 220 the Sharing preview shows, scaled
        // to the phone's width, so the point the organiser picked lands where
        // they saw it.
        <div style={{ ...(isMobile ? { aspectRatio: '390 / 220' } : { height: 240 }), overflow: 'hidden', backgroundColor: 'var(--fvd-neutral-chip)' }}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={image}
            alt={`${eventName} banner`}
            onError={() => setFailed(image)}
            style={{
              display: 'block', width: '100%', height: '100%', objectFit: 'cover',
              objectPosition: (isMobile ? bannerPosition?.mobile : bannerPosition?.desktop) || '50% 50%',
            }}
          />
        </div>
      )}
      <div style={{ maxWidth: 560, margin: '0 auto', padding: '24px 16px 0', boxSizing: 'border-box' }}>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 500, color: 'var(--fvd-ink-muted)' }}>{eyebrow}</p>
        <h1 style={{ margin: '4px 0 0', fontSize: 24, fontWeight: 600, letterSpacing: '-0.028em', lineHeight: 1.2, color: 'var(--fv-photo-heading, var(--fvd-ink))', textWrap: 'balance' }}>
          {eventName}
        </h1>
        {(subtitle || badge) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
            {subtitle && <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: 'var(--fvd-ink-secondary)' }}>{subtitle}</p>}
            {badge && (
              <span
                style={{
                  display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 8px',
                  borderRadius: 'var(--fv-radius-chip, var(--fvd-radius-chip))', background: 'var(--fvd-neutral-chip)',
                  color: 'var(--fvd-ink-secondary)', fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap',
                }}
              >
                {badge}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
